import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { EditUserDialog } from '@/components/admin/EditUserDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Users, Shield, Mail, Calendar, ShieldCheck, ShieldAlert, UserCog, History, Plus, Minus, Pencil, CheckSquare, XSquare, UserPlus } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import { Checkbox } from '@/components/ui/checkbox';

type AppRole = 'patient' | 'supervisor' | 'agency_admin' | 'super_admin';

interface UserWithRoles {
  id: string;
  user_id: string;
  email: string;
  is_active: boolean;
  created_at: string;
  agency_id: string | null;
  agency_name: string | null;
  roles: AppRole[];
}

interface RoleChangeConfirmation {
  user: UserWithRoles;
  newRole: AppRole;
  action: 'add' | 'remove';
}

interface RoleChangeLog {
  id: string;
  created_at: string;
  action: string;
  target_id: string | null;
  actor_user_id: string | null;
  actor_email?: string;
  metadata: {
    email?: string;
    role?: string;
    action?: string;
  } | null;
}

const ROLE_LABELS: Record<AppRole, string> = {
  patient: 'Patient',
  supervisor: 'Supervisor',
  agency_admin: 'Agency Admin',
  super_admin: 'Super Admin',
};

const ROLE_COLORS: Record<AppRole, string> = {
  patient: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  supervisor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  agency_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  super_admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const ROLE_ICONS: Record<AppRole, typeof Shield> = {
  patient: Users,
  supervisor: ShieldCheck,
  agency_admin: UserCog,
  super_admin: ShieldAlert,
};

export default function AdminUserRolesPage() {
  const { user } = useAdminAuth();
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [confirmingChange, setConfirmingChange] = useState<RoleChangeConfirmation | null>(null);
  const [isChanging, setIsChanging] = useState(false);
  const [roleChangeLogs, setRoleChangeLogs] = useState<RoleChangeLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [editingUser, setEditingUser] = useState<UserWithRoles | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAgencyAdmin = user?.role === 'agency_admin';
  const canManageRoles = isSuperAdmin || isAgencyAdmin;

  // Roles that agency admins can assign (supervisor and patient only)
  const allowedRolesForAgencyAdmin: AppRole[] = ['supervisor', 'patient'];

  useEffect(() => {
    if (canManageRoles) {
      loadUsers();
      if (isSuperAdmin) {
        loadRoleChangeLogs();
      }
    }
  }, [user]);

  const loadRoleChangeLogs = async () => {
    setLogsLoading(true);
    try {
      // Get role change logs
      const { data: logs, error: logsError } = await supabase
        .from('admin_audit_logs')
        .select('id, created_at, action, target_id, actor_user_id, metadata')
        .in('action', ['role_added', 'role_removed'])
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) throw logsError;

      // Get actor emails
      const actorIds = [...new Set((logs || []).map(l => l.actor_user_id).filter(Boolean))];
      let actorEmailMap = new Map<string, string>();
      
      if (actorIds.length > 0) {
        const { data: actors } = await supabase
          .from('profiles')
          .select('user_id, email')
          .in('user_id', actorIds);
        
        actorEmailMap = new Map((actors || []).map(a => [a.user_id, a.email]));
      }

      // Map logs with actor emails
      const logsWithEmails: RoleChangeLog[] = (logs || []).map(log => ({
        id: log.id,
        created_at: log.created_at,
        action: log.action,
        target_id: log.target_id,
        actor_user_id: log.actor_user_id,
        actor_email: log.actor_user_id ? actorEmailMap.get(log.actor_user_id) : undefined,
        metadata: log.metadata as RoleChangeLog['metadata'],
      }));

      setRoleChangeLogs(logsWithEmails);
    } catch (error) {
      console.error('Error loading role change logs:', error);
      toast.error('Failed to load role change history');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Get all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, email, is_active, created_at, agency_id')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Get all user roles
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Get all agencies for names
      const { data: agencies, error: agenciesError } = await supabase
        .from('agencies')
        .select('id, name');

      if (agenciesError) throw agenciesError;

      // Create a map of agency names
      const agencyMap = new Map(agencies?.map(a => [a.id, a.name]) || []);

      // Combine data
      const usersWithRoles: UserWithRoles[] = (profiles || []).map(p => {
        const userRoles = roles?.filter(r => r.user_id === p.user_id).map(r => r.role as AppRole) || [];
        return {
          id: p.id,
          user_id: p.user_id,
          email: p.email,
          is_active: p.is_active,
          created_at: p.created_at,
          agency_id: p.agency_id,
          agency_name: p.agency_id ? agencyMap.get(p.agency_id) || null : null,
          roles: userRoles,
        };
      });

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!confirmingChange) return;

    setIsChanging(true);
    try {
      const { user: targetUser, newRole, action } = confirmingChange;

      if (action === 'add') {
        // Add role
        const { error } = await supabase
          .from('user_roles')
          .insert({
            user_id: targetUser.user_id,
            role: newRole,
            assigned_by: user?.id,
          });

        if (error) {
          console.error('Role insert error:', error);
          throw new Error(error.message || 'Failed to add role');
        }

        // Update local state
        setUsers(prev => prev.map(u => 
          u.user_id === targetUser.user_id 
            ? { ...u, roles: [...u.roles, newRole] }
            : u
        ));

        toast.success(`Added ${ROLE_LABELS[newRole]} role to ${targetUser.email}`);
      } else {
        // Remove role
        const { error } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', targetUser.user_id)
          .eq('role', newRole);

        if (error) {
          console.error('Role delete error:', error);
          throw new Error(error.message || 'Failed to remove role');
        }

        // Update local state
        setUsers(prev => prev.map(u => 
          u.user_id === targetUser.user_id 
            ? { ...u, roles: u.roles.filter(r => r !== newRole) }
            : u
        ));

        toast.success(`Removed ${ROLE_LABELS[newRole]} role from ${targetUser.email}`);
      }

      // Log the audit event
      await supabase.from('admin_audit_logs').insert({
        actor_user_id: user?.id,
        action: action === 'add' ? 'role_added' : 'role_removed',
        target_resource: 'user_roles',
        target_id: targetUser.user_id,
        metadata: {
          email: targetUser.email,
          role: newRole,
          action: action,
        },
      });

      // Refresh logs
      loadRoleChangeLogs();

    } catch (error: any) {
      console.error('Error changing role:', error);
      toast.error(error.message || 'Failed to change role');
    } finally {
      setIsChanging(false);
      setConfirmingChange(null);
    }
  };

  const toggleRole = (targetUser: UserWithRoles, role: AppRole) => {
    const hasRole = targetUser.roles.includes(role);
    setConfirmingChange({
      user: targetUser,
      newRole: role,
      action: hasRole ? 'remove' : 'add',
    });
  };

  const handleEditUser = (targetUser: UserWithRoles) => {
    setEditingUser(targetUser);
    setEditDialogOpen(true);
  };

  const handleUserUpdated = () => {
    loadUsers();
    loadRoleChangeLogs();
  };

  // Bulk selection handlers
  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.user_id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const getSelectedUsersList = () => {
    return filteredUsers.filter(u => selectedUsers.has(u.user_id));
  };

  // Bulk actions
  const handleBulkActivate = async (activate: boolean) => {
    const selected = getSelectedUsersList();
    if (selected.length === 0) return;

    setIsBulkProcessing(true);
    try {
      const userIds = selected.map(u => u.user_id);
      
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: activate, updated_at: new Date().toISOString() })
        .in('user_id', userIds);

      if (error) throw error;

      // Log audit events
      for (const u of selected) {
        await supabase.from('admin_audit_logs').insert({
          actor_user_id: user?.id,
          action: activate ? 'user_activated' : 'user_deactivated',
          target_resource: 'profiles',
          target_id: u.user_id,
          metadata: { email: u.email, bulk_action: true },
        });
      }

      toast.success(`${selected.length} user(s) ${activate ? 'activated' : 'deactivated'}`);
      loadUsers();
      clearSelection();
    } catch (error) {
      console.error('Error in bulk action:', error);
      toast.error('Failed to update users');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkAddRole = async (role: AppRole) => {
    const selected = getSelectedUsersList();
    if (selected.length === 0) return;

    setIsBulkProcessing(true);
    try {
      let successCount = 0;
      let skipCount = 0;

      for (const u of selected) {
        if (u.roles.includes(role)) {
          skipCount++;
          continue;
        }

        const { error } = await supabase
          .from('user_roles')
          .insert({ user_id: u.user_id, role, assigned_by: user?.id });

        if (!error) {
          successCount++;
          await supabase.from('admin_audit_logs').insert({
            actor_user_id: user?.id,
            action: 'role_added',
            target_resource: 'user_roles',
            target_id: u.user_id,
            metadata: { email: u.email, role, bulk_action: true },
          });
        }
      }

      if (successCount > 0) {
        toast.success(`Added ${ROLE_LABELS[role]} role to ${successCount} user(s)${skipCount > 0 ? `, ${skipCount} skipped (already had role)` : ''}`);
      } else if (skipCount > 0) {
        toast.info(`All selected users already have the ${ROLE_LABELS[role]} role`);
      }

      loadUsers();
      loadRoleChangeLogs();
      clearSelection();
    } catch (error) {
      console.error('Error in bulk role assignment:', error);
      toast.error('Failed to assign roles');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleBulkRemoveRole = async (role: AppRole) => {
    const selected = getSelectedUsersList();
    if (selected.length === 0) return;

    setIsBulkProcessing(true);
    try {
      let successCount = 0;
      let skipCount = 0;

      for (const u of selected) {
        if (!u.roles.includes(role)) {
          skipCount++;
          continue;
        }

        const { error } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', u.user_id)
          .eq('role', role);

        if (!error) {
          successCount++;
          await supabase.from('admin_audit_logs').insert({
            actor_user_id: user?.id,
            action: 'role_removed',
            target_resource: 'user_roles',
            target_id: u.user_id,
            metadata: { email: u.email, role, bulk_action: true },
          });
        }
      }

      if (successCount > 0) {
        toast.success(`Removed ${ROLE_LABELS[role]} role from ${successCount} user(s)${skipCount > 0 ? `, ${skipCount} skipped (didn't have role)` : ''}`);
      } else if (skipCount > 0) {
        toast.info(`None of the selected users have the ${ROLE_LABELS[role]} role`);
      }

      loadUsers();
      loadRoleChangeLogs();
      clearSelection();
    } catch (error) {
      console.error('Error in bulk role removal:', error);
      toast.error('Failed to remove roles');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.agency_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesRole = roleFilter === 'all' || u.roles.includes(roleFilter as AppRole);
    return matchesSearch && matchesRole;
  });

  const getRoleCount = (role: AppRole) => users.filter(u => u.roles.includes(role)).length;

  if (!canManageRoles) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </AdminLayout>
    );
  }

  // Roles to show in stats and filters - agency admins only see supervisor/patient
  const visibleRoles: AppRole[] = isSuperAdmin 
    ? ['super_admin', 'agency_admin', 'supervisor', 'patient']
    : ['supervisor', 'patient'];

  // Roles that can be assigned by current user
  const assignableRoles: AppRole[] = isSuperAdmin 
    ? ['super_admin', 'agency_admin', 'supervisor', 'patient']
    : allowedRolesForAgencyAdmin;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-8 w-8" />
              User Roles Management
            </h1>
            <p className="text-muted-foreground mt-1">
              {isSuperAdmin 
                ? 'View and manage user roles across all agencies'
                : `Manage user roles for ${user?.agency?.name || 'your agency'}`
              }
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`grid grid-cols-2 ${isSuperAdmin ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-4`}>
          {visibleRoles.map(role => {
            const Icon = ROLE_ICONS[role];
            return (
              <Card key={role} className="shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${ROLE_COLORS[role]}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{getRoleCount(role)}</p>
                      <p className="text-sm text-muted-foreground">{ROLE_LABELS[role]}s</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users & Roles
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="history" className="gap-2">
                <History className="h-4 w-4" />
                Change History
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="users" className="space-y-6">
            {/* Filters */}
            <Card className="shadow-card">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email or agency..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {visibleRoles.map(role => (
                        <SelectItem key={role} value={role}>{ROLE_LABELS[role]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Actions Bar */}
            {selectedUsers.size > 0 && (
              <Card className="shadow-card border-primary/50 bg-primary/5">
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-5 w-5 text-primary" />
                      <span className="font-medium">
                        {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
                      </span>
                      <Button variant="ghost" size="sm" onClick={clearSelection}>
                        Clear
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkActivate(true)}
                        disabled={isBulkProcessing}
                        className="gap-1"
                      >
                        <CheckSquare className="h-4 w-4" />
                        Activate
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkActivate(false)}
                        disabled={isBulkProcessing}
                        className="gap-1"
                      >
                        <XSquare className="h-4 w-4" />
                        Deactivate
                      </Button>
                      <Select
                        value=""
                        onValueChange={(value) => handleBulkAddRole(value as AppRole)}
                        disabled={isBulkProcessing}
                      >
                        <SelectTrigger className="w-36 h-9">
                          <div className="flex items-center gap-1">
                            <UserPlus className="h-4 w-4" />
                            <span>Add Role</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {(['super_admin', 'agency_admin', 'supervisor', 'patient'] as AppRole[]).map(role => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value=""
                        onValueChange={(value) => handleBulkRemoveRole(value as AppRole)}
                        disabled={isBulkProcessing}
                      >
                        <SelectTrigger className="w-40 h-9">
                          <div className="flex items-center gap-1">
                            <Minus className="h-4 w-4" />
                            <span>Remove Role</span>
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {(['super_admin', 'agency_admin', 'supervisor', 'patient'] as AppRole[]).map(role => (
                            <SelectItem key={role} value={role}>
                              {ROLE_LABELS[role]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Users Table */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No users found</p>
                    <p className="text-sm mt-1">Try a different search term or filter</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all users"
                          />
                        </TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Agency</TableHead>
                        <TableHead>Current Roles</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((targetUser) => (
                        <TableRow key={targetUser.id} className={selectedUsers.has(targetUser.user_id) ? 'bg-primary/5' : ''}>
                          <TableCell>
                            <Checkbox
                              checked={selectedUsers.has(targetUser.user_id)}
                              onCheckedChange={() => toggleUserSelection(targetUser.user_id)}
                              aria-label={`Select ${targetUser.email}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {targetUser.email.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  <Mail className="h-4 w-4 text-muted-foreground" />
                                  {targetUser.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {targetUser.agency_name || 'No Agency'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {targetUser.roles.length > 0 ? (
                                targetUser.roles.map(role => (
                                  <Badge key={role} className={ROLE_COLORS[role]}>
                                    {ROLE_LABELS[role]}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-sm text-muted-foreground">No roles</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={targetUser.is_active ? 'default' : 'secondary'}
                              className={targetUser.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : ''
                              }
                            >
                              {targetUser.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(targetUser.created_at), 'MMM d, yyyy')}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(targetUser)}
                                className="gap-1"
                              >
                                <Pencil className="h-3 w-3" />
                                Edit
                              </Button>
                              <Select
                                value=""
                                onValueChange={(value) => toggleRole(targetUser, value as AppRole)}
                              >
                                <SelectTrigger className="w-36">
                                  <SelectValue placeholder="Manage Roles" />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignableRoles.map(role => {
                                    const hasRole = targetUser.roles.includes(role);
                                    return (
                                      <SelectItem key={role} value={role}>
                                        {hasRole ? '✓ Remove' : '+ Add'} {ROLE_LABELS[role]}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="shadow-card bg-muted/50">
              <CardContent className="pt-6">
                <div className="flex gap-4">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-foreground">Role Hierarchy</h3>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li><strong>Super Admin:</strong> Full platform access, manage all agencies, users, and settings</li>
                      <li><strong>Agency Admin:</strong> Manage their agency's patients, supervisors, and billing</li>
                      <li><strong>Supervisor:</strong> Manage patients and view triage sessions within their agency</li>
                      <li><strong>Patient:</strong> Access their own health information and triage assessments</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            {/* Role Change History */}
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Role Change History
                </CardTitle>
                <CardDescription>
                  Recent role additions and removals (last 50 changes)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
                    ))}
                  </div>
                ) : roleChangeLogs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No role changes recorded</p>
                    <p className="text-sm mt-1">Role changes will appear here when you modify user roles</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Target User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Performed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roleChangeLogs.map((log) => {
                        const isAdded = log.action === 'role_added';
                        const role = log.metadata?.role as AppRole | undefined;
                        return (
                          <TableRow key={log.id}>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                {format(new Date(log.created_at), 'MMM d, yyyy HH:mm')}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                className={isAdded 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }
                              >
                                <span className="flex items-center gap-1">
                                  {isAdded ? <Plus className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                                  {isAdded ? 'Added' : 'Removed'}
                                </span>
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{log.metadata?.email || 'Unknown'}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {role && ROLE_LABELS[role] ? (
                                <Badge className={ROLE_COLORS[role]}>
                                  {ROLE_LABELS[role]}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">Unknown</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {log.actor_email || 'System'}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Role Change Confirmation Dialog */}
      <AlertDialog open={!!confirmingChange} onOpenChange={(open) => !open && setConfirmingChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingChange?.action === 'add' ? 'Add Role' : 'Remove Role'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {confirmingChange?.action === 'add' ? 'add' : 'remove'} the{' '}
              <span className="font-semibold text-foreground">
                {confirmingChange && ROLE_LABELS[confirmingChange.newRole]}
              </span>{' '}
              role {confirmingChange?.action === 'add' ? 'to' : 'from'}{' '}
              <span className="font-semibold text-foreground">{confirmingChange?.user.email}</span>?
              {confirmingChange?.newRole === 'super_admin' && (
                <span className="block mt-2 text-destructive">
                  ⚠️ Warning: Super Admin role grants full platform access.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isChanging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleChange}
              disabled={isChanging}
              className={confirmingChange?.action === 'remove' && confirmingChange?.newRole === 'super_admin'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : ''
              }
            >
              {isChanging ? 'Processing...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        user={editingUser}
        onUserUpdated={handleUserUpdated}
        currentUserId={user?.id}
      />
    </AdminLayout>
  );
}
